/*
 * Growplay native frame builder (replaces the CPB app).
 *
 * Reads the JSON rendered by snippets/frame-builder.liquid, stacks the picture
 * layers by their rules, lets the shopper pick add-ons (live price/stock from
 * the linked variants) and adds the frame plus every add-on to the cart in one
 * request, each add-on nested under the frame so it is its own order line.
 *
 * Rules (same as tools/builder-logic.js in the cpb-migration branch):
 *   sw  shown with    - visible only if ANY of these add-ons is selected (empty = no condition)
 *   ar  also requires - ...and also ANY of these
 *   hw  hidden with   - hidden if ANY of these is selected
 *   adv advanced rule - optional CPB-style fallback { action, rules: [{ join, negate, addons }] }
 *
 * Mounted by assets/gp-builder-loader.js when the builder is opened; signals
 * readiness with a `gp:native-builder-mounted` event on document.body.
 */
(() => {
  const root = document.querySelector('#product-builder[data-gp-native-builder]');
  if (!root || root.dataset.gpNbMounted === 'true') return;
  root.dataset.gpNbMounted = 'true';
  // Page-level listeners belong to this copy of the builder: when the theme
  // editor re-renders the section, the previous copy's listeners are removed.
  if (window.__gpNbListeners) window.__gpNbListeners.abort();
  const listeners = new AbortController();
  window.__gpNbListeners = listeners;
  const on = (target, type, fn, options = {}) => target.addEventListener(type, fn, { ...options, signal: listeners.signal });

  const data = JSON.parse(root.querySelector('[data-gp-nb-data]').textContent);
  const frameConfig = window.GrowplayFramePdp || {};
  const $ = (sel) => root.querySelector(sel);
  const debug = root.dataset.gpNbDebug === 'true' || new URLSearchParams(location.search).get('builder') === 'debug';
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // On frame pages the builder is only on screen while open; skip scroll work otherwise.
  const html = document.documentElement;
  const isActive = () => !html.classList.contains('gp-frame-pdp') || html.classList.contains('gp-frame-builder-open');

  // Desktop add-on layout: 'list' (one column of rows), 'cards' (two columns) or
  // 'tiles' (three columns, big pictures). Phones always use the list. Staff can
  // compare with ?layout=list|cards|tiles, which also shows a small switcher.
  const LAYOUTS = ['list', 'cards', 'tiles'];
  const DEFAULT_LAYOUT = 'tiles';
  const layoutParam = new URLSearchParams(location.search).get('layout');
  let layout = LAYOUTS.includes(layoutParam) ? layoutParam : DEFAULT_LAYOUT;
  try {
    if (LAYOUTS.includes(layoutParam)) sessionStorage.setItem('gp-nb-layout', layoutParam);
    else if (LAYOUTS.includes(sessionStorage.getItem('gp-nb-layout'))) layout = sessionStorage.getItem('gp-nb-layout');
  } catch (e) { /* storage blocked: use the default */ }
  root.classList.add(`gp-nb--${layout}`);
  // Colour of picked add-ons; staff compare with ?accent=green|sage|mustard|orange|beige (mustard is the default).
  const ACCENTS = { green: '#566449', sage: '#899977', mustard: '#f2b544', orange: '#e07b39', beige: '#e3d3b2' };
  const DEFAULT_ACCENT = 'mustard';
  const isAccent = (name) => Object.prototype.hasOwnProperty.call(ACCENTS, name || '');
  const accentParam = new URLSearchParams(location.search).get('accent');
  let accent = isAccent(accentParam) ? accentParam : DEFAULT_ACCENT;
  try {
    if (isAccent(accentParam)) sessionStorage.setItem('gp-nb-accent', accentParam);
    else if (isAccent(sessionStorage.getItem('gp-nb-accent'))) accent = sessionStorage.getItem('gp-nb-accent');
  } catch (e) { /* storage blocked: use the default */ }
  root.dataset.accent = accent;
  const GROUP_ORDER = ['Swings & rides', 'Climbing & ninja', 'Sports & games', 'Frame upgrades', 'More add-ons'];

  // ------------------------------------------------------------------ data
  const addons = data.addons.slice().sort((a, b) => a.sort - b.sort);
  const byKey = new Map(addons.map((a) => [a.key, a]));
  const byVariant = new Map(addons.map((a) => [a.variantId, a]));
  const layers = data.layers.slice().sort((a, b) => a.order - b.order);
  // Add-ons that come with this product (e.g. the XL Pro's extras): always on
  // for the picture and the rules, never charged or added to the cart.
  const included = (data.included || []).filter((a) => a.key);
  const includedKeys = included.map((a) => a.key);
  const includedByKey = new Map(included.map((a) => [a.key, a]));
  const label = (k) => (byKey.get(k) || includedByKey.get(k) || { label: k }).label;
  let shownTitle = data.title;
  // "A, B or C" for choices; "A, B and C" for things that happened together.
  const names = (keys, joiner = 'or') => listNames(keys.map(label), joiner);

  function listNames(list, joiner = 'or') {
    if (list.length <= 1) return list.join('');
    return `${list.slice(0, -1).join(', ')} ${joiner} ${list[list.length - 1]}`;
  }
  const removedNote = (dropped) => (dropped.length
    ? ` ${names(dropped, 'and')} ${dropped.length === 1 ? 'was' : 'were'} removed, as ${dropped.length === 1 ? 'it' : 'they'} can't be combined.`
    : '');

  // Whole amounts without decimals ($130), otherwise always two ($49.50, not $49.5).
  const money = (() => {
    const make = (digits) => {
      try {
        return new Intl.NumberFormat(data.locale || undefined, { style: 'currency', currency: data.currency || 'AUD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: digits, maximumFractionDigits: digits });
      } catch (e) {
        return { format: (v) => `$${v.toFixed(digits)}` };
      }
    };
    const whole = make(0);
    const exact = make(2);
    return (cents) => {
      const value = Math.round(Number(cents) || 0);
      return (value % 100 === 0 ? whole : exact).format(value / 100);
    };
  })();

  const stageCol = $('.gp-nb__stage-col');
  const stage = $('.gp-nb__stage');
  const bar = $('[data-gp-nb-bar]');
  const preview = $('[data-gp-nb-preview]');

  const escapeHtml = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  // ------------------------------------------------------------------ frame variant
  // Frames with several variants (e.g. Playhouse Basic / Complete Kit) use the
  // one picked on the product page.
  const frameVariants = new Map((data.frame.variants || []).map((v) => [Number(v.id), v]));
  function syncFrameVariant() {
    if (frameVariants.size < 2) return false;
    const input = [...document.querySelectorAll('form[action*="/cart/add"] [name="id"]')]
      .find((el) => !root.contains(el) && frameVariants.has(Number(el.value)));
    const v = input && frameVariants.get(Number(input.value));
    if (!v || v.id === data.frame.variantId) return false;
    Object.assign(data.frame, { variantId: v.id, sku: v.sku, price: v.price, compareAt: v.compareAt, available: v.available, variantTitle: v.title });
    return true;
  }
  syncFrameVariant();

  // Several packages (e.g. Playhouse Basic / Complete Kit): pick it in the
  // builder too; the product page's own picker follows, and the other way round.
  function setFrameVariant(v) {
    Object.assign(data.frame, { variantId: v.id, sku: v.sku, price: v.price, compareAt: v.compareAt, available: v.available, variantTitle: v.title });
  }
  let updatingPage = false; // our own change events, not the shopper's
  function selectPageVariant(v) {
    updatingPage = true;
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (root.contains(form)) return;
      form.querySelectorAll('select').forEach((select) => {
        const match = [...select.options].find((o) => o.value === String(v.id) || o.value === v.title);
        if (match && select.value !== match.value) {
          select.value = match.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
    updatingPage = false;
  }

  // ------------------------------------------------------------------ rules
  const anySelected = (keys, selected) => (keys || []).filter((k) => selected.has(k));

  // Returns { visible, why, blockedBy } — blockedBy is set when the item would
  // otherwise show but another selection excludes it (shown greyed out).
  function evaluate(item, selected) {
    const sw = item.sw || [], ar = item.ar || [], hw = item.hw || [];
    if (item.adv && Array.isArray(item.adv.rules) && item.adv.rules.length) {
      const chunks = [[]];
      item.adv.rules.forEach((r, i) => {
        if (i > 0 && r.join === '&&') chunks.push([]);
        const hit = (r.addons || []).some((k) => selected.has(k));
        chunks[chunks.length - 1].push(r.negate ? !hit : hit);
      });
      const met = chunks.every((c) => c.some(Boolean));
      const visible = item.adv.action === 'hide' ? !met : met;
      return { visible, why: `${visible ? 'Shown' : 'Hidden'} by its advanced rule` };
    }
    if (sw.length && !anySelected(sw, selected).length) return { visible: false, why: `Needs ${names(sw)}` };
    if (ar.length && !anySelected(ar, selected).length) return { visible: false, why: `Also needs ${names(ar)}` };
    const blockers = anySelected(hw, selected);
    if (blockers.length) return { visible: false, blockedBy: blockers, why: `Hidden because ${names(blockers)} is selected` };
    if (sw.length) return { visible: true, why: `Shown with ${names(anySelected(sw, selected))}` };
    return { visible: true, why: hw.length ? `Shown unless ${names(hw)} is selected` : 'Always shown' };
  }

  // ------------------------------------------------------------------ free gift
  // "Choose 1 of 3" promo (custom.choose_your_adventure) wins over auto-added
  // gifts (custom.free_addons). Only gifts in stock count: if every choice is
  // sold out we fall back to the auto gifts, then to no gift at all, so the
  // shopper is never asked to pick from an empty list.
  const giftList = (list, limit) => (Array.isArray(list) ? list : []).slice(0, limit).map((meta) => {
    const id = Number(meta && (meta.variant_id || meta.variantId)) || 0;
    const addon = byVariant.get(id);
    return {
      variantId: id,
      handle: (meta && meta.handle) || (addon && addon.productHandle) || '',
      key: addon ? addon.key : null,
      label: (addon && addon.label) || String((meta && meta.title) || 'Free add-on').replace(/^free\s+/i, ''),
      thumb: (addon && addon.thumb) || (meta && meta.image) || '',
      value: Number((meta && (meta.compare_at_cents || meta.compareAt)) || 0) || (addon ? addon.compareAt || addon.price : 0),
      available: !!id && meta.available !== false && (!addon || addon.available)
    };
  }).filter((g) => g.variantId);
  const giftCandidates = { choose: giftList(frameConfig.choose_adventure, 3), auto: giftList(frameConfig.free_addons, 99) };
  let giftMode = null;
  let gifts = [];
  let chosenGift = [];
  function resolveGifts() {
    const choose = giftCandidates.choose.filter((g) => g.available);
    const auto = giftCandidates.auto.filter((g) => g.available);
    giftMode = choose.length ? 'choose' : auto.length ? 'auto' : null;
    gifts = giftMode === 'choose' ? choose : giftMode === 'auto' ? auto : [];
    chosenGift = giftMode === 'auto'
      ? gifts.map((g) => g.variantId)
      : chosenGift.filter((id) => gifts.some((g) => g.variantId === id)); // keep a choice that's still in stock
  }
  resolveGifts();

  // ------------------------------------------------------------------ state
  const selected = new Set();
  const giftKeys = () => chosenGift.map((id) => gifts.find((g) => g.variantId === id)).filter((g) => g && g.key).map((g) => g.key);
  const effectiveSelection = () => new Set([...selected, ...giftKeys(), ...includedKeys]);
  const sellable = (a) => a.offered && a.available; // sold-out add-ons stay hidden, as with CPB

  // Drop selections a rule has since excluded (e.g. Cubby House excludes Fireman Pole).
  function settle() {
    const dropped = [];
    // The free gift replaces a paid copy of the same add-on (a remembered or shared
    // setup can still name it), so the gift is never added a second time at full price.
    giftKeys().forEach((k) => selected.delete(k));
    for (let i = 0; i < 5; i += 1) {
      const sel = effectiveSelection();
      const before = selected.size;
      [...selected].forEach((k) => {
        const a = byKey.get(k);
        if (!a || !sellable(a) || !evaluate(a, sel).visible) { selected.delete(k); dropped.push(k); }
      });
      if (selected.size === before) break;
    }
    return dropped;
  }

  // ------------------------------------------------------------------ sticky offset
  // Keep the picture and jump links clear of any sticky/fixed site header.
  // Measured values live here (CSS gets copies as custom properties).
  const geo = { top: 0, topMax: 0, collapse: 0, stageH: 0 };
  const stackedQuery = window.matchMedia('(max-width: 989px)');
  const setVar = (el, name, value) => { if (el.style.getPropertyValue(name) !== value) el.style.setProperty(name, value); };
  // Pinned bars: candidates are found once (again on resize/open); which of them
  // are fixed/sticky is re-checked at most every 250ms, as themes switch headers
  // to fixed only after scrolling starts. Per frame only their rects are read.
  let barCandidates = null;
  let pinnedBars = [];
  let pinnedCheckedAt = -Infinity;
  function refreshPinnedBars(force) {
    const now = performance.now();
    if (!force && now - pinnedCheckedAt < 250) return;
    pinnedCheckedAt = now;
    if (!barCandidates) {
      barCandidates = [...document.querySelectorAll('header, [class*="header"], [data-sticky]')]
        .filter((el) => !root.contains(el) && !el.closest('#product-builder'));
    }
    pinnedBars = barCandidates.filter((el) => { const p = getComputedStyle(el).position; return p === 'fixed' || p === 'sticky'; });
  }
  function updateTopOffset(force) {
    refreshPinnedBars(force);
    // Reads first, then writes, so the browser lays out once.
    const bars = pinnedBars
      .map((el) => el.getBoundingClientRect())
      .filter((r) => r.height > 0 && r.height < window.innerHeight / 3)
      .sort((a, b) => a.top - b.top);
    let offset = 0;
    // Pinned bars stack (header, then the sticky email bar under it): keep extending
    // the offset while they touch (small gaps allowed: the email bar rests 12px down
    // when the header slides away).
    bars.forEach((r) => { if (r.top <= offset + 24 && r.bottom > offset) offset = r.bottom; });
    geo.top = Math.round(offset);
    // The desktop picture is sized for the tallest header seen, so it doesn't
    // resize while the header slides in and out.
    geo.topMax = Math.max(geo.topMax, geo.top);
    // On small screens the picture is pinned too, at its collapsed size, and the
    // category tabs sit under it.
    const stacked = stackedQuery.matches;
    geo.collapse = 0;
    geo.stageH = 0;
    if (stacked) {
      const small = Math.round(Math.max(190, Math.min(window.innerHeight * 0.33, 290)));
      geo.collapse = Math.max(0, stage.offsetHeight - small);
      geo.stageH = Math.round(stageCol.offsetHeight - geo.collapse);
    }
    const barH = Math.round(bar.offsetHeight);
    setVar(root, '--gp-nb-top', `${geo.top}px`);
    setVar(root, '--gp-nb-top-max', `${geo.topMax}px`);
    setVar(root, '--gp-nb-collapse', `${geo.collapse}px`);
    setVar(root, '--gp-nb-stage-h', `${geo.stageH}px`);
    setVar(root, '--gp-nb-bar-h', `${barH}px`);
    collapseStage();
  }

  // Phones: the picture opens full width, then shrinks into its pinned strip as
  // the shopper scrolls into the add-ons. The column keeps its height (no page
  // jumps); only the picture inside is moved and scaled to fit what's visible.
  function collapseStage() {
    if (!geo.collapse) { if (preview.style.transform) preview.style.transform = ''; return; }
    const hidden = Math.min(geo.collapse, Math.max(0, geo.top - stage.getBoundingClientRect().top));
    const h = stage.offsetHeight;
    // The button strip above the picture (preview.offsetTop) scrolls away with the
    // back and share buttons, so it shrinks to nothing as the stage collapses and
    // the picture takes the whole pinned height.
    const strip = preview.offsetTop * (1 - hidden / geo.collapse);
    const s = (h - hidden - strip) / (h - preview.offsetTop);
    const transform = hidden ? `translateY(${hidden + strip - preview.offsetTop}px) scale(${s})` : '';
    if (preview.style.transform !== transform) preview.style.transform = transform;
  }
  // One handler for every scroll-driven update, at most once per frame.
  let frameQueued = false;
  let settleTimer;
  on(window, 'scroll', () => {
    if (!isActive()) return;
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => updateTopOffset(true), 320); // the header slides in and out on scroll
    if (frameQueued) return;
    frameQueued = true;
    requestAnimationFrame(() => {
      frameQueued = false;
      updateTopOffset();
      spy();
    });
  }, { passive: true });
  on(window, 'resize', () => {
    barCandidates = null;
    geo.topMax = 0;
    if (isActive()) { updateTopOffset(true); updateTabsOverflow(); }
  });

  // ------------------------------------------------------------------ picture
  const loading = $('[data-gp-nb-loading]');
  const imgs = new Map();
  layers.forEach((layer) => {
    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;
    img.dataset.layer = layer.key;
    img.hidden = true;
    preview.appendChild(img);
    imgs.set(layer.key, img);
  });
  // Picture crop ("x,y,width,height" in canvas px, from the builder's Picture
  // crop field): the empty margin around the frame is cut off so the frame
  // fills the stage. The stage takes the crop's shape (see --gp-nb-vb-ratio);
  // every layer is drawn at canvas size, shifted so the crop fills the stage.
  const canvasSize = Number(data.canvas) || 1500;
  const viewBox = (() => {
    const v = String(data.viewBox || '').split(',').map((n) => parseFloat(n));
    if (v.length !== 4 || v.some((n) => !Number.isFinite(n)) || v[2] <= 0 || v[3] <= 0) return null;
    return { x: v[0], y: v[1], w: v[2], h: v[3] };
  })();
  if (viewBox) stage.style.setProperty('--gp-nb-vb-ratio', (viewBox.w / viewBox.h).toFixed(4));
  // Fits the crop to the stage and tells the browser how large the layers are
  // drawn, so it picks the right image width for the screen.
  let drawnSize = '';
  function fitPicture() {
    const W = preview.clientWidth;
    const H = preview.clientHeight;
    if (!W || !H) return;
    let size = W;
    if (viewBox) {
      const k = Math.min(W / viewBox.w, H / viewBox.h);
      size = canvasSize * k;
      preview.classList.add('is-cropped');
      setVar(preview, '--gp-nb-crop-size', `${size.toFixed(1)}px`);
      setVar(preview, '--gp-nb-crop-left', `${((W - viewBox.w * k) / 2 - viewBox.x * k).toFixed(1)}px`);
      setVar(preview, '--gp-nb-crop-top', `${((H - viewBox.h * k) / 2 - viewBox.y * k).toFixed(1)}px`);
    }
    const sizes = `${Math.ceil(size)}px`;
    if (sizes !== drawnSize) { drawnSize = sizes; imgs.forEach((img) => { img.sizes = sizes; }); }
  }
  fitPicture();
  if ('ResizeObserver' in window) new ResizeObserver(fitPicture).observe(preview);
  else on(window, 'resize', fitPicture);

  // Layer images come in three widths from one CDN URL.
  const sized = (src, width) => {
    try {
      const url = new URL(src, location.href);
      url.searchParams.set('width', width);
      return url.href;
    } catch (e) {
      return src;
    }
  };
  const loadImg = (layer) => {
    const img = imgs.get(layer.key);
    if (img.getAttribute('src')) return;
    if (!img.sizes) img.sizes = drawnSize || '100vw';
    img.srcset = [750, 1100, 1500].map((w) => `${sized(layer.src, w)} ${w}w`).join(', ');
    img.src = sized(layer.src, 1100);
  };

  let firstPaint = true;
  function paint(sel) {
    const states = layers.map((layer) => ({ layer, ...evaluate(layer, sel) }));
    const pending = [];
    states.forEach(({ layer, visible }) => {
      const img = imgs.get(layer.key);
      if (visible) {
        loadImg(layer);
        if (img.hidden && !firstPaint && !reduceMotion) {
          img.classList.add('is-entering');
          requestAnimationFrame(() => requestAnimationFrame(() => img.classList.remove('is-entering')));
        }
        if (firstPaint && !img.complete) pending.push(new Promise((r) => { img.addEventListener('load', r, { once: true }); img.addEventListener('error', r, { once: true }); }));
      }
      img.hidden = !visible;
    });
    if (firstPaint) {
      firstPaint = false;
      Promise.race([Promise.all(pending), new Promise((r) => setTimeout(r, 6000))]).then(() => loading.classList.add('is-done'));
    }
    return states;
  }

  // ------------------------------------------------------------------ zoom
  // Computers: click the picture to magnify it 2x in place, following the
  // mouse; click again or move off the picture to zoom back out. It stays in
  // its spot so the add-on choices stay in view.
  const zoomQuery = window.matchMedia('(min-width: 990px) and (pointer: fine)');
  const aimZoom = (event) => {
    const r = stage.getBoundingClientRect();
    const top = r.top + preview.offsetTop;
    const x = Math.min(100, Math.max(0, ((event.clientX - r.left) / r.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - top) / (r.bottom - top)) * 100));
    preview.style.transformOrigin = `${x}% ${y}%`;
  };
  const unzoom = () => { preview.classList.remove('is-zoomed'); preview.style.transformOrigin = ''; };
  on(preview, 'click', (event) => {
    if (!zoomQuery.matches) return;
    if (preview.classList.contains('is-zoomed')) { unzoom(); return; }
    aimZoom(event);
    // Ask for sharper layer images at the zoomed size (the srcset goes up to 1500px).
    const zoomedSize = `${Math.ceil(parseFloat(drawnSize || '0') * 2)}px`;
    if (parseFloat(drawnSize) && drawnSize !== zoomedSize) { drawnSize = zoomedSize; imgs.forEach((img) => { img.sizes = zoomedSize; }); }
    preview.classList.add('is-zoomed');
  });
  on(preview, 'mousemove', (event) => { if (preview.classList.contains('is-zoomed')) aimZoom(event); });
  on(preview, 'mouseleave', unzoom);
  const markZoomable = () => { preview.classList.toggle('is-zoomable', zoomQuery.matches); if (!zoomQuery.matches) unzoom(); };
  markZoomable();
  on(zoomQuery, 'change', markZoomable);

  // Preload only the layers one more tap could show (adding or removing any
  // single add-on, or choosing a gift), so the next change is instant without
  // downloading pictures for combinations nobody has picked.
  let warmQueued = false;
  function warmNext() {
    if (warmQueued) return;
    warmQueued = true;
    const run = () => {
      warmQueued = false;
      const sel = effectiveSelection();
      const pending = layers.filter((layer) => !imgs.get(layer.key).getAttribute('src'));
      if (!pending.length) return;
      const tappable = [...tiles].filter(([, opt]) => !opt.hidden && opt.getAttribute('aria-disabled') !== 'true').map(([key]) => key);
      const taps = [...tappable, ...gifts.map((g) => g.key).filter(Boolean)];
      taps.forEach((key) => {
        const next = new Set(sel);
        if (next.has(key)) next.delete(key); else next.add(key);
        pending.forEach((layer) => { if (evaluate(layer, next).visible) loadImg(layer); });
      });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 500);
  }

  // ------------------------------------------------------------------ add-on options (built once)
  const groupsHost = $('[data-gp-nb-groups]');
  const tabsHost = $('[data-gp-nb-jump]');
  const tiles = new Map();
  const sections = new Map();
  // Title case to match the site navigation ("Swings & Rides", "More Add-ons").
  const groupName = (g) => g.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const ICON_PLUS = '<svg class="gp-nb__ico-plus" viewBox="0 0 20 20" width="16" height="16" focusable="false"><path d="M10 4.5v11M4.5 10h11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  const ICON_CHECK = '<svg class="gp-nb__ico-check" viewBox="0 0 20 20" width="16" height="16" focusable="false"><path d="m5 10.5 3.2 3.2L15 6.8" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function priceMarkup(price, compareAt) {
    const was = compareAt > price ? ` <s>${money(compareAt)}</s>` : '';
    return `<span class="gp-nb__opt-price">${money(price)}</span>${was}`;
  }

  function optionInner({ thumb, name, meta, control }) {
    return `
      <span class="gp-nb__opt-media">${thumb ? `<img src="${escapeHtml(thumb)}" alt="" loading="lazy" width="128" height="128">` : ''}</span>
      <span class="gp-nb__opt-body">
        <span class="gp-nb__opt-name">${escapeHtml(name)}</span>
        <span class="gp-nb__opt-meta">${meta}</span>
      </span>
      ${control}`;
  }

  function buildTiles() {
    groupsHost.innerHTML = '';
    tabsHost.innerHTML = '';
    tiles.clear();
    sections.clear();
    const groups = [...new Set(addons.filter((a) => a.offered).map((a) => a.group))]
      .sort((a, b) => (GROUP_ORDER.indexOf(a) + 1 || 99) - (GROUP_ORDER.indexOf(b) + 1 || 99));
    groups.forEach((group) => {
      const id = `gp-nb-group-${slug(group)}`;
      const section = document.createElement('section');
      section.className = 'gp-nb__group';
      section.id = id;
      section.setAttribute('aria-labelledby', `${id}-title`);
      section.innerHTML = `<h3 class="gp-nb__group-title" id="${id}-title">${escapeHtml(groupName(group))}</h3><div class="gp-nb__list"></div>`;
      const list = section.querySelector('.gp-nb__list');
      addons.filter((a) => a.offered && a.group === group).forEach((a) => {
        const opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'gp-nb__opt';
        opt.dataset.key = a.key;
        opt.innerHTML = optionInner({
          thumb: a.thumb,
          name: a.label,
          meta: priceMarkup(a.price, a.compareAt),
          control: `<span class="gp-nb__opt-ctl" aria-hidden="true">${ICON_PLUS}${ICON_CHECK}</span>`
        });
        // A sold-out add-on can't be picked: tapping it offers a back-in-stock email instead.
        opt.addEventListener('click', () => (opt.classList.contains('is-soldout') ? openNotify(a.key) : toggleAddon(a.key)));
        opt.dataset.order = list.children.length;
        list.appendChild(opt);
        tiles.set(a.key, opt);
      });
      groupsHost.appendChild(section);

      const tab = document.createElement('a');
      tab.className = 'gp-nb__tab';
      tab.href = `#${id}`;
      tab.innerHTML = `<span>${escapeHtml(groupName(group))}</span><span class="gp-nb__tab-count" hidden></span>`;
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        tappedGroup = group;
        spy();
        section.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        section.querySelector('.gp-nb__opt:not([hidden]):not([aria-disabled="true"])')?.focus({ preventScroll: true });
      });
      tabsHost.appendChild(tab);
      sections.set(group, { section, chip: tab });
    });
  }

  // An option ruled out by another pick can be swapped in, unless the free gift rules it out.
  const swapFor = (ev) => (ev.blockedBy && ev.blockedBy.every((k) => selected.has(k)) ? ev.blockedBy : null);

  // ------------------------------------------------------------------ back in stock
  // One sign-up form, opened right under the sold-out tile that was tapped, so the
  // shopper never leaves the builder. Sign-ups go to Klaviyo's back-in-stock list
  // for that variant, the same as the form on the product pages.
  const notify = { key: null, form: document.createElement('form') };
  notify.form.className = 'gp-nb__notify';
  notify.form.id = 'gp-nb-notify';
  notify.form.noValidate = true;
  notify.form.hidden = true;
  notify.form.innerHTML = `
    <p class="gp-nb__notify-title" data-gp-nb-notify-title></p>
    <div class="gp-nb__notify-row">
      <label class="gp-nb__sr" for="gp-nb-notify-email">Email</label>
      <input class="gp-nb__notify-email" id="gp-nb-notify-email" type="email" name="email" autocomplete="email" inputmode="email" placeholder="Your email" required>
      <button class="gp-nb__notify-send" type="submit">Notify me</button>
    </div>
    <p class="gp-nb__notify-msg" data-gp-nb-notify-msg role="status"></p>
    <button class="gp-nb__notify-close" type="button" aria-label="Close" data-gp-nb-notify-close><span aria-hidden="true">&times;</span></button>`;
  const notifyInput = notify.form.querySelector('input');
  const notifySend = notify.form.querySelector('[type="submit"]');
  const notifyMsg = notify.form.querySelector('[data-gp-nb-notify-msg]');
  function setNotifyMsg(text, isError) {
    notifyMsg.textContent = text || '';
    notifyMsg.classList.toggle('is-error', !!isError);
  }
  function closeNotify(returnFocus) {
    const tile = notify.key && tiles.get(notify.key);
    notify.key = null;
    notify.form.hidden = true;
    notify.form.remove();
    showGhost(null);
    if (tile) {
      tile.setAttribute('aria-expanded', 'false');
      if (returnFocus) tile.focus({ preventScroll: true });
    }
  }
  // The form goes under the whole row of the tapped tile (in the tile grid, after
  // the row's last tile), so the row stays together and the form opens below it.
  function placeNotify() {
    const tile = notify.key && tiles.get(notify.key);
    if (!tile) return;
    notify.form.remove();
    let end = tile;
    for (let next = tile.nextElementSibling; next; next = next.nextElementSibling) {
      if (next.hidden) continue;
      if (Math.abs(next.offsetTop - tile.offsetTop) > 2) break;
      end = next;
    }
    end.after(notify.form);
  }
  on(window, 'resize', () => { if (notify.key) placeNotify(); });
  function openNotify(key) {
    const a = byKey.get(key);
    const tile = tiles.get(key);
    if (!a || !tile) return;
    if (notify.key === key) { closeNotify(true); return; }
    if (notify.key) closeNotify();
    notify.key = key;
    notify.form.querySelector('[data-gp-nb-notify-title]').innerHTML = `Get an email when <strong>${escapeHtml(a.label)}</strong> is back in stock.`;
    setNotifyMsg('');
    notify.form.hidden = false;
    placeNotify();
    showGhost(key);
    tile.setAttribute('aria-expanded', 'true');
    notifyInput.focus({ preventScroll: true });
    // Bring the form clear of the bottom bar, after the tap's own handling is done.
    requestAnimationFrame(() => {
      const r = notify.form.getBoundingClientRect();
      const limit = Math.min(window.innerHeight, bar.getBoundingClientRect().top) - 12;
      if (r.bottom > limit) window.scrollBy(0, r.bottom - limit);
    });
  }
  notify.form.querySelector('[data-gp-nb-notify-close]').addEventListener('click', () => closeNotify(true));
  notify.form.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation(); // closes the form only; the builder stays open
    closeNotify(true);
  });
  notify.form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const a = byKey.get(notify.key);
    const email = notifyInput.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNotifyMsg('Please enter a valid email address.', true);
      notifyInput.focus();
      return;
    }
    if (!a || !data.klaviyoCompanyId) return;
    notifySend.disabled = true;
    setNotifyMsg('');
    try {
      const res = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(data.klaviyoCompanyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: { profile: { data: { type: 'profile', attributes: { email } } }, channels: ['EMAIL'] },
            relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${a.variantId}` } } }
          }
        })
      });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).errors[0].detail || ''; } catch (e) { /* no details */ }
        throw new Error(detail);
      }
      notifyInput.value = '';
      setNotifyMsg(`You're on the list. We'll email you when ${a.label} is back.`);
      const key = notify.key;
      setTimeout(() => { if (notify.key === key) closeNotify(true); }, 2600);
    } catch (error) {
      setNotifyMsg(error.message || 'Something went wrong. Please try again.', true);
    } finally {
      notifySend.disabled = false;
    }
  });

  function updateTiles(sel) {
    const giftSet = new Set(giftKeys());
    const counts = new Map();
    tiles.forEach((opt, key) => {
      const a = byKey.get(key);
      const ev = evaluate(a, sel);
      const blocked = !ev.visible && ev.blockedBy && ev.blockedBy.length;
      const show = a.offered && !giftSet.has(key) && (ev.visible || blocked);
      opt.hidden = !show;
      if (!show) return;
      // Sold out: shown greyed at the end of its group, with a back-in-stock sign-up.
      const soldOut = !a.available;
      opt.classList.toggle('is-soldout', soldOut);
      if (soldOut) {
        opt.removeAttribute('aria-pressed');
        opt.setAttribute('aria-disabled', 'false');
        opt.setAttribute('aria-expanded', notify.key === key ? 'true' : 'false');
        opt.setAttribute('aria-controls', 'gp-nb-notify');
        opt.classList.remove('is-selected', 'is-blocked', 'is-swap');
        // "Sold out" is the label where the add button sits (CSS); the line under the name offers the email.
        opt.querySelector('.gp-nb__opt-meta').innerHTML = '<span class="gp-nb__opt-notify">Notify me</span>';
        opt.setAttribute('aria-label', `${a.label}, sold out. Get an email when it's back.`);
        return;
      }
      opt.removeAttribute('aria-expanded');
      opt.removeAttribute('aria-controls');
      const on = selected.has(key);
      const swap = blocked && swapFor(ev);
      opt.setAttribute('aria-pressed', on ? 'true' : 'false');
      opt.setAttribute('aria-disabled', blocked && !swap ? 'true' : 'false');
      opt.classList.toggle('is-selected', on);
      opt.classList.toggle('is-blocked', !!blocked && !swap);
      opt.classList.toggle('is-swap', !!swap);
      opt.querySelector('.gp-nb__opt-meta').innerHTML = swap
        ? `${priceMarkup(a.price, a.compareAt)} <span class="gp-nb__opt-note">Replaces ${escapeHtml(names(swap, 'and'))}</span>`
        : blocked
          ? `<span class="gp-nb__opt-note">Not available with ${escapeHtml(names(ev.blockedBy))}</span>`
          : priceMarkup(a.price, a.compareAt);
      opt.setAttribute('aria-label', swap
        ? `${a.label}, ${money(a.price)}. Choosing it replaces ${names(swap, 'and')}.`
        : blocked
          ? `${a.label}, ${money(a.price)}. Not available with ${names(ev.blockedBy)}.`
          : `${a.label}, ${money(a.price)}`);
      if (on) counts.set(a.group, (counts.get(a.group) || 0) + 1);
    });
    sections.forEach(({ section, chip }, group) => {
      // Sold-out tiles go last, the rest keep their order. The DOM only moves when
      // the order changes, so focus isn't lost on every repaint.
      const list = section.querySelector('.gp-nb__list');
      const opts = [...list.querySelectorAll(':scope > .gp-nb__opt')];
      const rank = (t) => (t.classList.contains('is-soldout') ? 1000 : 0) + Number(t.dataset.order);
      const sorted = [...opts].sort((x, y) => rank(x) - rank(y));
      if (sorted.some((t, i) => t !== opts[i])) {
        sorted.forEach((t) => list.appendChild(t));
        if (notify.key && list.contains(tiles.get(notify.key))) placeNotify();
      }
      const any = opts.some((t) => !t.hidden);
      section.hidden = !any;
      chip.hidden = !any;
      const count = chip.querySelector('.gp-nb__tab-count');
      const n = counts.get(group) || 0;
      count.hidden = !n;
      count.textContent = n;
    });
    tabsWrap.hidden = ![...sections.values()].some(({ chip }) => !chip.hidden);
    // Back in stock (or hidden by a rule) while its sign-up form is open: close it.
    if (notify.key) {
      const t = tiles.get(notify.key);
      if (!t || t.hidden || !t.classList.contains('is-soldout')) closeNotify();
    }
    spy();
    updateTabsOverflow();
  }

  // Highlight the category tab for the section in view. A tapped tab stays
  // highlighted until the shopper scrolls themselves: a short category near the
  // end can't scroll up to the line.
  let tappedGroup = null;
  ['wheel', 'touchstart', 'keydown'].forEach((type) => window.addEventListener(type, () => { tappedGroup = null; }, { passive: true }));
  function spy() {
    const line = geo.top + geo.stageH + tabsHost.offsetHeight + 24;
    let current = null;
    sections.forEach(({ section }, group) => {
      if (!section.hidden && section.getBoundingClientRect().top <= line) current = group;
    });
    if (!current) current = [...sections.keys()].find((g) => !sections.get(g).section.hidden);
    // Scrolled to the end: a short last category may never reach the line.
    const shown = [...sections.keys()].filter((g) => !sections.get(g).section.hidden);
    if (shown.length && groupsHost.getBoundingClientRect().bottom <= window.innerHeight - bar.offsetHeight + 2) current = shown[shown.length - 1];
    if (tappedGroup && shown.includes(tappedGroup)) current = tappedGroup;
    sections.forEach(({ chip }, group) => {
      const on = group === current;
      chip.classList.toggle('is-current', on);
      if (on) chip.setAttribute('aria-current', 'true'); else chip.removeAttribute('aria-current');
    });
    const active = tabsHost.querySelector('.is-current');
    if (active && tabsHost.scrollWidth > tabsHost.clientWidth) {
      const left = active.offsetLeft - (tabsHost.clientWidth - active.offsetWidth) / 2;
      if (Math.abs(tabsHost.scrollLeft - left) > 4) tabsHost.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }
  // Tabs that don't fit scroll sideways: fade the cut-off edge and offer arrows.
  const tabsWrap = tabsHost.parentElement;
  function updateTabsOverflow() {
    const max = tabsHost.scrollWidth - tabsHost.clientWidth;
    tabsWrap.classList.toggle('has-more-start', tabsHost.scrollLeft > 4);
    tabsWrap.classList.toggle('has-more-end', max - tabsHost.scrollLeft > 4);
  }
  tabsHost.addEventListener('scroll', updateTabsOverflow, { passive: true });
  tabsWrap.querySelectorAll('[data-gp-nb-tabs-step]').forEach((btn) => btn.addEventListener('click', () => {
    tabsHost.scrollBy({ left: Number(btn.dataset.gpNbTabsStep) * tabsHost.clientWidth * 0.6, behavior: reduceMotion ? 'auto' : 'smooth' });
  }));

  // ------------------------------------------------------------------ free gift
  const giftHost = $('[data-gp-nb-gift-tiles]');
  giftHost.addEventListener('keydown', (event) => {
    if (giftMode !== 'choose' || !/^Arrow(Up|Down|Left|Right)$/.test(event.key)) return;
    const cards = [...giftHost.querySelectorAll('.gp-nb__gift-card')];
    const i = cards.indexOf(document.activeElement);
    if (i < 0) return;
    event.preventDefault();
    const next = cards[(i + (/Down|Right/.test(event.key) ? 1 : -1) + cards.length) % cards.length];
    next.click();
    next.focus();
  });
  function buildGift() {
    const box = $('[data-gp-nb-gift]');
    giftHost.innerHTML = '';
    if (!giftMode || !gifts.length) { box.hidden = true; return; }
    box.hidden = false;
    $('[data-gp-nb-gift-title]').textContent = giftMode === 'choose' ? 'Choose your free add-on' : 'Free with your frame';
    giftHost.setAttribute('role', giftMode === 'choose' ? 'radiogroup' : 'list');
    if (giftMode === 'choose') giftHost.setAttribute('aria-labelledby', 'gp-nb-gift-title');
    gifts.forEach((g) => {
      const choose = giftMode === 'choose';
      const el = document.createElement(choose ? 'button' : 'div');
      el.className = `gp-nb__opt gp-nb__gift-card${choose ? ' is-choice' : ' is-included'}`;
      if (choose) { el.type = 'button'; el.setAttribute('role', 'radio'); } else el.setAttribute('role', 'listitem');
      el.dataset.key = g.key || '';
      el.dataset.variant = g.variantId;
      el.innerHTML = optionInner({
        thumb: g.thumb,
        name: g.label,
        meta: `${g.value ? `<s>${money(g.value)}</s> ` : ''}<span class="gp-nb__free">Free</span>`,
        control: choose
          ? '<span class="gp-nb__opt-radio" aria-hidden="true"></span>'
          : `<span class="gp-nb__opt-tag">${ICON_CHECK}Included</span>`
      });
      if (choose) {
        el.addEventListener('click', () => {
          chosenGift = [g.variantId];
          if (g.key) selected.delete(g.key); // the gift replaces a paid copy of the same item
          const dropped = render();
          announce(`${g.label} chosen as your free add-on.${removedNote(dropped)}`);
          toast(`${g.label} added free`);
        });
      }
      giftHost.appendChild(el);
    });
  }

  function buildIncluded() {
    const box = $('[data-gp-nb-included]');
    const host = $('[data-gp-nb-included-tiles]');
    host.innerHTML = '';
    box.hidden = !included.length;
    included.forEach((a) => {
      const el = document.createElement('div');
      el.className = 'gp-nb__opt gp-nb__gift-card is-included';
      el.setAttribute('role', 'listitem');
      el.innerHTML = optionInner({
        thumb: a.thumb,
        name: a.label,
        meta: a.value ? `<span class="gp-nb__opt-price">${money(a.value)} value</span>` : '',
        control: `<span class="gp-nb__opt-tag">${ICON_CHECK}Included</span>`
      });
      host.appendChild(el);
    });
  }

  function updateGift() {
    const cards = [...giftHost.querySelectorAll('.gp-nb__gift-card')];
    cards.forEach((el, i) => {
      const on = chosenGift.includes(Number(el.dataset.variant));
      el.classList.toggle('is-selected', on);
      if (giftMode === 'choose') {
        el.setAttribute('aria-checked', on ? 'true' : 'false');
        // Radio group: one tab stop (the chosen card, else the first); arrows move between cards.
        el.tabIndex = on || (!chosenGift.length && i === 0) ? 0 : -1;
      }
    });
    $('[data-gp-nb-gift]').classList.toggle('needs-choice', giftMode === 'choose' && !chosenGift.length);
  }

  // ------------------------------------------------------------------ package picker
  const kitsHost = document.createElement('div');
  function buildKits() {
    if (frameVariants.size < 2) return;
    kitsHost.className = 'gp-nb__kits';
    kitsHost.setAttribute('role', 'radiogroup');
    kitsHost.setAttribute('aria-label', 'Package');
    $('.gp-nb__price-line').after(kitsHost);
    kitsHost.addEventListener('keydown', (event) => {
      if (!/^Arrow(Up|Down|Left|Right)$/.test(event.key)) return;
      const buttons = [...kitsHost.querySelectorAll('button')];
      const i = buttons.indexOf(document.activeElement);
      if (i < 0) return;
      event.preventDefault();
      const next = buttons[(i + (/Down|Right/.test(event.key) ? 1 : -1) + buttons.length) % buttons.length];
      next.click();
      next.focus();
    });
    renderKits();
  }
  function renderKits() {
    if (frameVariants.size < 2) return;
    kitsHost.innerHTML = '';
    frameVariants.forEach((v) => {
      const on = v.id === data.frame.variantId;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `gp-nb__kit${on ? ' is-selected' : ''}`;
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
      b.disabled = !v.available;
      b.innerHTML = `<span class="gp-nb__kit-name">${escapeHtml(v.title)}</span><span class="gp-nb__kit-price">${v.available ? money(v.price) : 'Sold out'}</span>`;
      b.addEventListener('click', () => {
        if (v.id === data.frame.variantId) return;
        setFrameVariant(v);
        selectPageVariant(v);
        render();
        announce(`${v.title} selected. Total ${money(totals().total)}.`);
      });
      kitsHost.appendChild(b);
    });
  }
  // The product page's picker changed: follow it.
  on(document, 'change', (event) => {
    const form = event.target.closest && event.target.closest('form[action*="/cart/add"]');
    if (updatingPage || !form || root.contains(form)) return;
    setTimeout(() => { if (syncFrameVariant()) render(); }, 0);
  });

  // ------------------------------------------------------------------ "added" confirmation over the picture
  const toastEl = document.createElement('div');
  toastEl.className = 'gp-nb__toast';
  toastEl.setAttribute('aria-hidden', 'true');
  stage.appendChild(toastEl);
  let toastTimer;
  function toast(text, removed, ms = 1800) {
    toastEl.innerHTML = `${removed ? '' : ICON_CHECK}<span>${escapeHtml(text)}</span>`;
    toastEl.classList.toggle('is-removed', !!removed);
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-on'), ms);
  }

  // ------------------------------------------------------------------ sold-out preview
  // While a sold-out add-on's sign-up form is open, the picture shows the setup
  // as it would be with it: its own layers faded, anything that moves to make
  // room for it (e.g. the XL's gym rings going to the accessory bar) drawn as
  // normal, and anything it would replace taken out. A tag says it's sold out.
  // The setup and total don't change.
  const ghostTag = document.createElement('div');
  ghostTag.className = 'gp-nb__ghost-tag';
  ghostTag.setAttribute('aria-hidden', 'true');
  stage.appendChild(ghostTag);
  const GHOST_CLASSES = ['is-ghost', 'is-ghost-in', 'is-ghost-out'];
  // A layer is the add-on's own picture when it shows with that add-on alone.
  const ownLayer = (layer, key) => (layer.adv && Array.isArray(layer.adv.rules)
    ? layer.adv.rules.some((r) => !r.negate && (r.addons || []).includes(key))
    : (layer.sw || []).length === 1 && layer.sw[0] === key);
  let ghostKey = null;
  function showGhost(key) {
    const sel = effectiveSelection();
    imgs.forEach((img, layerKey) => {
      if (!GHOST_CLASSES.some((c) => img.classList.contains(c))) return;
      img.classList.remove(...GHOST_CLASSES);
      img.hidden = !evaluate(layers.find((l) => l.key === layerKey), sel).visible;
    });
    const a = key && byKey.get(key);
    const fresh = key !== ghostKey;
    ghostKey = a ? key : null;
    ghostTag.classList.toggle('is-on', !!a);
    if (!a) return;
    ghostTag.textContent = `Preview: ${a.label} (sold out)`;
    const swap = swapFor(evaluate(a, sel));
    const next = new Set(sel);
    if (swap) swap.forEach((k) => next.delete(k));
    next.add(key);
    layers.forEach((layer) => {
      const img = imgs.get(layer.key);
      const now = evaluate(layer, sel).visible;
      const then = evaluate(layer, next).visible;
      if (then && !now) {
        loadImg(layer);
        img.hidden = false;
        img.classList.add(ownLayer(layer, key) ? 'is-ghost' : 'is-ghost-in');
        if (fresh && !reduceMotion) {
          img.classList.add('is-entering');
          requestAnimationFrame(() => requestAnimationFrame(() => img.classList.remove('is-entering')));
        }
      } else if (now && !then) {
        img.hidden = true;
        img.classList.add('is-ghost-out');
      }
    });
  }

  // ------------------------------------------------------------------ price + summary
  function totals() {
    let total = data.frame.price;
    let was = Math.max(data.frame.compareAt, data.frame.price);
    let giftValue = 0;
    selected.forEach((k) => {
      const a = byKey.get(k);
      if (!a) return;
      total += a.price;
      was += Math.max(a.compareAt, a.price);
    });
    chosenGift.forEach((id) => {
      const g = gifts.find((x) => x.variantId === id);
      if (g) giftValue += g.value;
    });
    // was = the frame and add-ons at full price, total = what's paid. The free
    // gift is shown on its own card, not in the saving.
    return { total, was, giftValue };
  }

  function lineItem({ img, title, sub, price, removeKey }) {
    const li = document.createElement('li');
    li.className = 'gp-nb__line';
    li.innerHTML = `
      <span class="gp-nb__line-media">${img ? `<img src="${escapeHtml(img)}" alt="" width="48" height="48" loading="lazy">` : ''}</span>
      <span class="gp-nb__line-text"><span class="gp-nb__line-title">${escapeHtml(title)}</span>${sub ? `<span class="gp-nb__line-sub">${escapeHtml(sub)}</span>` : ''}</span>
      <span class="gp-nb__line-price">${price}</span>
      ${removeKey ? `<button type="button" class="gp-nb__line-remove" aria-label="Remove ${escapeHtml(label(removeKey))}"><svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="m5.5 5.5 9 9M14.5 5.5l-9 9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>` : '<span class="gp-nb__line-remove" aria-hidden="true"></span>'}`;
    if (removeKey) {
      const btn = li.querySelector('button');
      btn.addEventListener('click', () => {
        // The list re-renders: keep keyboard focus in the sheet (next item, else Close).
        const removeButtons = () => [...root.querySelectorAll('[data-gp-nb-lines] button.gp-nb__line-remove')];
        const index = removeButtons().indexOf(btn);
        toggleAddon(removeKey);
        const after = removeButtons();
        (after[index] || after[index - 1] || $('[data-gp-nb-setup-close]')).focus({ preventScroll: true });
      });
    }
    return li;
  }

  function renderSummary() {
    const { total, was, giftValue } = totals();
    const save = was > total ? was - total : 0;
    const giftNote = $('[data-gp-nb-giftnote]');
    const giftNames = chosenGift.map((id) => gifts.find((g) => g.variantId === id)).filter(Boolean).map((g) => g.label);
    giftNote.hidden = !giftValue;
    giftNote.textContent = giftValue ? `Includes a free ${listNames(giftNames, 'and')} (${money(giftValue)} value)` : '';
    $('[data-gp-nb-total]').textContent = money(total);
    $('[data-gp-nb-was]').textContent = save ? money(was) : '';
    const saveEl = $('[data-gp-nb-save]');
    saveEl.hidden = !save;
    saveEl.textContent = save ? `Save ${money(save)}` : '';
    $('[data-gp-nb-bar-total]').textContent = money(total);
    $('[data-gp-nb-sheet-total]').textContent = money(total);
    $('[data-gp-nb-bar-was]').textContent = save ? money(was) : '';

    // Setup lines
    const lines = $('[data-gp-nb-lines]');
    lines.innerHTML = '';
    lines.appendChild(lineItem({ img: data.frame.image, title: shownTitle, sub: data.frame.variantTitle ? `Frame · ${data.frame.variantTitle}` : 'Frame', price: priceMarkup(data.frame.price, data.frame.compareAt) }));
    chosenGift.forEach((id) => {
      const g = gifts.find((x) => x.variantId === id);
      if (g) lines.appendChild(lineItem({ img: g.thumb, title: g.label, sub: 'Free gift', price: '<span class="gp-nb__free">Free</span>' }));
    });
    const picked = [...selected].map((k) => byKey.get(k)).filter(Boolean);
    picked.forEach((a) => lines.appendChild(lineItem({ img: a.thumb, title: a.label, sub: groupName(a.group), price: priceMarkup(a.price, a.compareAt), removeKey: a.key })));
    const items = 1 + chosenGift.length + picked.length;
    $('[data-gp-nb-setup-count]').textContent = `View setup (${items})`;
    // Starts with the visible words, so voice control ("click View setup") works.
    $('[data-gp-nb-setup-toggle]').setAttribute('aria-label', `View setup (${items}), total ${money(total)}`);
    $('[data-gp-nb-clear]').hidden = !picked.length;

    // Button
    const add = $('[data-gp-nb-add]');
    const needsGift = giftMode === 'choose' && !chosenGift.length;
    const busy = add.dataset.busy === 'true';
    add.disabled = !data.frame.available || busy;
    add.setAttribute('aria-disabled', needsGift ? 'true' : 'false');
    add.classList.toggle('is-busy', busy);
    add.textContent = busy ? 'Adding…'
      : !data.frame.available ? 'Sold Out'
      : needsGift ? 'Choose Your Free Add-on'
      : 'Add to Cart';

    // Payment messaging (snippets/gp-bnpl.liquid) follows the total, in cents.
    root.querySelectorAll('[data-gp-nb-extra] gp-bnpl').forEach((el) => {
      if (el.dataset.amount !== String(total)) el.dataset.amount = String(total);
    });
    document.dispatchEvent(new CustomEvent('gp:native-builder-total', { detail: { total, was } }));
  }

  // ------------------------------------------------------------------ captions + debug
  function renderCaptions(sel) {
    const note = $('[data-gp-nb-captions]');
    const caps = (data.captions || [])
      .filter((c) => evaluate(c, sel).visible)
      .sort((a, b) => a.order - b.order)
      .map((c) => c.text)
      .filter(Boolean);
    note.hidden = !caps.length;
    note.textContent = caps.join(' ');
  }

  function renderDebug(states, sel) {
    const panel = $('[data-gp-nb-debug-panel]');
    if (!debug) return;
    panel.hidden = false;
    panel.innerHTML = `<p><strong>Builder debug</strong> · ${escapeHtml(data.handle)} · selected: ${sel.size ? escapeHtml(names([...sel])) : 'none'} · hover a row to highlight its layer</p>
      <ol reversed>${states.slice().reverse().map(({ layer, visible, why }) =>
        `<li class="${visible ? 'is-on' : 'is-off'}" data-layer="${escapeHtml(layer.key)}"><b>${visible ? 'Shown' : 'Hidden'}</b> ${escapeHtml(layer.title)} <span>(order ${layer.order}) ${escapeHtml(why)}</span></li>`).join('')}</ol>`;
    panel.querySelectorAll('li').forEach((li) => {
      li.addEventListener('mouseenter', () => preview.querySelectorAll('img').forEach((img) => img.classList.toggle('gp-nb__dim', img.dataset.layer !== li.dataset.layer)));
      li.addEventListener('mouseleave', () => preview.querySelectorAll('img').forEach((img) => img.classList.remove('gp-nb__dim')));
    });
  }

  // ------------------------------------------------------------------ render
  const live = $('[data-gp-nb-live]');
  function announce(text) {
    live.textContent = '';
    setTimeout(() => { live.textContent = text; }, 30);
  }

  const savedKey = `gp-nb:${data.handle}`;
  function saveSetup() {
    try {
      sessionStorage.setItem(savedKey, JSON.stringify({ selected: [...selected], gift: chosenGift, at: Date.now() }));
    } catch (e) { /* storage blocked: nothing to remember */ }
  }
  // A shared link (?setup=keys&gift=id) wins over what this visit remembered.
  function setupFromLink() {
    const params = new URLSearchParams(location.search);
    if (!params.has('setup')) return false;
    params.get('setup').split(',').forEach((k) => { if (byKey.has(k)) selected.add(k); });
    const gift = Number(params.get('gift'));
    if (giftMode === 'choose' && gifts.some((g) => g.variantId === gift)) chosenGift = [gift];
    return true;
  }
  function shareLink() {
    const url = new URL(location.pathname, location.origin);
    if (frameVariants.size > 1) url.searchParams.set('variant', data.frame.variantId);
    if (selected.size) url.searchParams.set('setup', [...selected].join(','));
    if (giftMode === 'choose' && chosenGift.length) url.searchParams.set('gift', chosenGift[0]);
    url.searchParams.set('customize', 'true');
    // On a preview theme, keep the preview so the link opens this builder on another device.
    const theme = window.Shopify && window.Shopify.theme;
    if (theme && theme.role && theme.role !== 'main' && theme.id) url.searchParams.set('preview_theme_id', theme.id);
    return url.href;
  }
  async function shareSetup() {
    const url = shareLink();
    const title = `My ${shownTitle} setup`;
    try {
      if (navigator.share && matchMedia('(pointer: coarse)').matches) { await navigator.share({ title, url }); return; }
      await navigator.clipboard.writeText(url);
      toast('Link copied');
      announce('Link to this setup copied.');
    } catch (e) {
      if (e && e.name === 'AbortError') return; // closed the share sheet
      window.prompt('Copy this link to your setup:', url);
    }
  }
  function restoreSetup() {
    if (setupFromLink()) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(savedKey) || 'null');
      if (!saved || Date.now() - saved.at > 6 * 3600 * 1000) return;
      (saved.selected || []).forEach((k) => { if (byKey.has(k)) selected.add(k); });
      if (giftMode === 'choose') chosenGift = (saved.gift || []).filter((id) => gifts.some((g) => g.variantId === id));
    } catch (e) { /* nothing saved */ }
  }

  function render() {
    const dropped = settle();
    const sel = effectiveSelection();
    const states = paint(sel);
    updateGift();
    updateTiles(sel);
    showGhost(notify.key);
    renderKits();
    renderSummary();
    renderCaptions(sel);
    renderDebug(states, sel);
    warmNext();
    saveSetup();
    return dropped;
  }

  function toggleAddon(key) {
    const a = byKey.get(key);
    const tile = tiles.get(key);
    if (!a || (tile && tile.getAttribute('aria-disabled') === 'true')) return;
    const swap = !selected.has(key) && swapFor(evaluate(a, effectiveSelection()));
    if (swap) {
      swap.forEach((k) => selected.delete(k));
      selected.add(key);
      const dropped = render();
      announce(`${a.label} added in place of ${names(swap, 'and')}.${removedNote(dropped)} Total ${money(totals().total)}.`);
      toast(`${a.label} swapped in`);
      return;
    }
    const adding = !selected.has(key);
    if (adding) selected.add(key); else selected.delete(key);
    const dropped = render();
    const { total } = totals();
    const extra = removedNote(dropped);
    announce(`${a.label} ${adding ? 'added' : 'removed'}.${extra} Total ${money(total)}.`);
    toast(adding ? `${a.label} added` : `${a.label} removed`, !adding);
  }

  // ------------------------------------------------------------------ cart
  function showError(message) {
    const el = $('[data-gp-nb-error]');
    // Shown first, then filled, so screen readers announce the alert.
    el.hidden = !message;
    el.textContent = message || '';
  }

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  async function postItems(items, attempt = 0) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items })
    });
    if (res.ok) return res.json();
    // Busy store: wait and try again before giving up.
    if (res.status === 429 && attempt < 2) { await wait(1500 * (attempt + 1)); return postItems(items, attempt + 1); }
    let detail = '';
    try { detail = (await res.json()).description || ''; } catch (e) { /* ignore */ }
    const error = new Error(detail || `Cart returned ${res.status}`);
    error.status = res.status;
    throw error;
  }

  function buildItems(cartVariantIds, nested) {
    const parent = data.frame.variantId;
    // A setup id keeps a second setup of the same frame on its own lines
    // instead of merging into the first (quantity 2 with mismatched add-ons).
    const frameProps = { _builder: `${data.handle}:v1`, _setup: Date.now().toString(36), _label: shownTitle };
    // The layers on screen, so the cart can show this setup's picture.
    const shown = layers.filter((layer) => !imgs.get(layer.key).hidden).map((layer) => layer.order);
    if (shown.length) frameProps._layers = shown.join(',');
    const status = String(frameConfig.backorder_status_text || '').trim();
    if (status) frameProps.Status = status;
    const items = [{ id: parent, quantity: 1, properties: frameProps }];
    const child = (item) => (nested ? { ...item, parent_id: parent } : { ...item, properties: { ...(item.properties || {}), _gp_parent: data.title } });
    [...selected].map((k) => byKey.get(k)).filter(Boolean).forEach((a) => items.push(child({ id: a.variantId, quantity: 1, properties: { _label: a.label } })));
    chosenGift.forEach((id) => {
      if (cartVariantIds.has(id)) return; // one promotional gift per order
      const g = gifts.find((x) => x.variantId === id);
      items.push(child({ id, quantity: 1, properties: { _gp_bonus_item: 'true', _gp_bonus_parent: data.title, _label: g ? g.label : '' } }));
    });
    return items;
  }

  async function addToCart() {
    const add = $('[data-gp-nb-add]');
    if (add.disabled) return;
    if (giftMode === 'choose' && !chosenGift.length) {
      const box = $('[data-gp-nb-gift]');
      box.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      box.querySelector('.gp-nb__gift-card')?.focus({ preventScroll: true });
      announce('Choose your free add-on first.');
      return;
    }
    showError('');
    add.dataset.busy = 'true';
    renderSummary();
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json()).catch(() => ({ items: [] }));
      const inCart = new Set((cart.items || []).map((i) => Number(i.variant_id)));
      try {
        await postItems(buildItems(inCart, true));
      } catch (error) {
        // Only when Shopify rejected the request itself (400/422, e.g. nesting not
        // supported) do we retry as flat lines. Anything else, including a dropped
        // connection where the first add may have gone through, is shown instead
        // of risking a second copy of the setup in the cart.
        if (!(error.status === 400 || error.status === 422) || /sold out|not available|cannot find|quantity/i.test(error.message)) throw error;
        await postItems(buildItems(inCart, false));
      }
      document.dispatchEvent(new CustomEvent('gp:native-builder-added', { detail: { handle: data.handle } }));
      // Back from the cart reopens the builder (with this setup, see restoreSetup).
      if (overlay) {
        try {
          const back = new URL(location.href);
          back.searchParams.set('customize', 'true');
          history.replaceState(history.state, '', back.href);
        } catch (e) { /* keep the URL as it is */ }
      }
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    } catch (error) {
      // Refused by the cart: something may have sold out since the builder opened.
      // Re-check stock; if that changed the setup, say what changed, so the next
      // Add to Cart goes through.
      if (error.status === 400 || error.status === 422) {
        const result = await refreshStock(true);
        if (result && (result.dropped.length || result.giftChanged || !data.frame.available)) {
          const removed = tellSoldOut(result.dropped);
          showError(!data.frame.available
            ? `Sorry, ${shownTitle} just sold out.`
            : removed
              ? `${removed} Your total is now ${money(totals().total)}. Check your setup, then add it to your cart again.`
              : 'Your free add-on just sold out. Check your free gift, then add your setup to your cart again.');
          add.dataset.busy = 'false';
          renderSummary();
          return;
        }
      }
      showError(/sold out|not available|quantity/i.test(error.message)
        ? `Something in your setup just sold out: ${error.message}. Please review your add-ons and try again.`
        : error.status === 429 || error.status >= 500 || !error.status
          ? 'The store is busy right now. Please try again in a moment.'
          : `We couldn't add this setup to your cart. ${error.message}`);
      add.dataset.busy = 'false';
      renderSummary();
    }
  }

  // Back from the cart: a page restored from the browser's cache would still say "Adding…".
  on(window, 'pageshow', (event) => {
    if (!event.persisted) return;
    checkCart();
    const add = $('[data-gp-nb-add]');
    if (add.dataset.busy !== 'true') return;
    add.dataset.busy = 'false';
    renderSummary();
  });

  // Tell the shopper when a setup of this frame is already in the cart, so a
  // second Add to Cart is a choice rather than an accident.
  async function checkCart() {
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      const inCart = (cart.items || []).some((item) => String((item.properties || {})._builder || '').startsWith(`${data.handle}:`));
      $('[data-gp-nb-incart]').hidden = !inCart;
    } catch (e) { /* leave the note hidden */ }
  }

  // Use the product page's display title (e.g. "The Large Playhouse" rather than
  // "LARGE PLAYHOUSE"); fall back to title-casing an all-caps product title.
  const heroTitle = [...document.querySelectorAll('h1.product-title, h1')].find((h) => !root.contains(h));
  const heroText = heroTitle ? [...heroTitle.childNodes].filter((n) => n.nodeType === 3).map((n) => n.textContent.trim()).find(Boolean) : '';
  const titleCase = (t) => (t === t.toUpperCase()
    ? t.toLowerCase().replace(/\b([a-z])([a-z]*)/g, (w, a, b) => (/^(xl|xxl)$/.test(w) ? w.toUpperCase() : a.toUpperCase() + b))
    : t);
  shownTitle = titleCase(heroText || data.title);
  // Only frame pages open the builder over the product page; elsewhere it's inline, with nothing to go back to.
  if (!document.documentElement.classList.contains('gp-frame-pdp')) $('[data-gp-nb-close]').hidden = true;
  if (shownTitle !== data.title) {
    $('[data-gp-nb-title]').textContent = shownTitle;
    $('[data-gp-nb-back-label]').textContent = `Back to ${shownTitle}`;
  }
  const barThumb = $('[data-gp-nb-bar-thumb]');
  if (data.frame.image) barThumb.src = data.frame.image; else barThumb.parentNode.hidden = true;

  // ------------------------------------------------------------------ backorder status (bottom bar, next to Add to cart)
  // Same source as the product page: the frame's custom.backorder_dispatch_date,
  // passed through window.GrowplayFramePdp. A date stays right on cached pages,
  // where a day count would go stale.
  function renderTrust() {
    const items = Array.isArray(frameConfig.primary_trust_items) ? frameConfig.primary_trust_items.filter(Boolean) : [];
    root.querySelectorAll('[data-gp-nb-trust]').forEach((el) => {
      el.hidden = !items.length;
      el.innerHTML = items.map((t) => `<span>${ICON_CHECK}${escapeHtml(t)}</span>`).join('');
    });
  }

  function renderShipping() {
    const box = $('[data-gp-nb-ship]');
    const status = String(frameConfig.backorder_status_text || '').trim();
    const iso = String(frameConfig.backorder_dispatch_date || '').trim();
    let text = '';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const when = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (when >= today) {
        let label;
        try { label = when.toLocaleDateString(data.locale || undefined, { weekday: 'short', month: 'short', day: 'numeric' }); } catch (e) { label = when.toDateString(); }
        text = `<strong>Backorder</strong> · dispatches by ${escapeHtml(label)}`;
      }
    }
    if (!text && status) text = `<strong>${escapeHtml(status)}</strong>`;
    box.hidden = !text;
    if (!text) return;
    $('[data-gp-nb-ship-text]').innerHTML = text;
    const details = String(frameConfig.backorder_status_details || '').trim();
    $('[data-gp-nb-ship-info]').hidden = !details;
    $('[data-gp-nb-ship-details]').textContent = details;
  }

  // ------------------------------------------------------------------ wire up
  $('[data-gp-nb-add]').addEventListener('click', addToCart);
  $('[data-gp-nb-close]').addEventListener('click', () => document.dispatchEvent(new CustomEvent('gp:builder-cancel')));
  $('[data-gp-nb-clear]').addEventListener('click', () => {
    selected.clear();
    render();
    if (!$('[data-gp-nb-setup]').hidden) $('[data-gp-nb-setup-close]').focus({ preventScroll: true });
    announce('All add-ons removed.');
    toast('All add-ons removed', true);
  });
  const setupToggle = $('[data-gp-nb-setup-toggle]');
  const scrim = $('[data-gp-nb-scrim]');
  const sheet = $('[data-gp-nb-setup]');
  const setOpen = (open) => {
    setupToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    sheet.hidden = !open;
    scrim.hidden = !open;
    setVar(root, '--gp-nb-bar-h', `${Math.round(bar.offsetHeight)}px`);
    root.classList.toggle('is-setup-open', open);
    html.classList.toggle('gp-nb-sheet-open', open);
    renderSummary();
    if (open) $('[data-gp-nb-setup-close]').focus({ preventScroll: true });
  };
  // Phones: a downward swipe on the sheet's top closes it.
  let swipeStart = null;
  sheet.addEventListener('touchstart', (event) => {
    swipeStart = sheet.scrollTop <= 0 && event.touches.length === 1 ? event.touches[0].clientY : null;
  }, { passive: true });
  sheet.addEventListener('touchend', (event) => {
    if (swipeStart === null) return;
    const dy = event.changedTouches[0].clientY - swipeStart;
    swipeStart = null;
    if (dy > 70) { setOpen(false); setupToggle.focus({ preventScroll: true }); }
  }, { passive: true });
  // Modal: Tab and Shift+Tab stay inside the open sheet.
  root.querySelectorAll('[data-gp-nb-share]').forEach((btn) => btn.addEventListener('click', shareSetup));
  sheet.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...sheet.querySelectorAll('button:not([hidden]), [href]')].filter((el) => !el.disabled && el.offsetParent);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  setupToggle.addEventListener('click', () => setOpen(setupToggle.getAttribute('aria-expanded') !== 'true'));
  scrim.addEventListener('click', () => { setOpen(false); setupToggle.focus({ preventScroll: true }); });
  $('[data-gp-nb-setup-close]').addEventListener('click', () => { setOpen(false); setupToggle.focus({ preventScroll: true }); });
  const shipInfo = $('[data-gp-nb-ship-info]');
  on(document, 'click', (event) => { if (shipInfo.open && !shipInfo.contains(event.target)) shipInfo.open = false; });
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && shipInfo.open) {
      event.preventDefault(); // handled here: the builder stays open
      shipInfo.open = false;
      shipInfo.querySelector('summary').focus();
      return;
    }
    if (event.key === 'Escape' && setupToggle.getAttribute('aria-expanded') === 'true') { event.preventDefault(); setOpen(false); setupToggle.focus(); }
  });

  // Start with the builder's top edge just below the pinned site header. The
  // product page also scrolls when the builder opens (without allowing for the
  // email bar), so wait until scrolling settles, then correct once, unless the
  // shopper has started scrolling themselves.
  let userScrolled = false;
  const markUser = () => { userScrolled = true; };
  function armAlign() {
    userScrolled = false;
    ['wheel', 'touchstart', 'keydown'].forEach((type) => on(window, type, markUser, { once: true, passive: true }));
  }
  // Only where the builder opens over the product page (frame pages); inline
  // builders on other products must never move the page.
  const overlay = document.documentElement.classList.contains('gp-frame-pdp');
  function alignToTop() {
    if (userScrolled || !overlay) return;
    updateTopOffset(true);
    const top = root.getBoundingClientRect().top;
    if (Math.abs(top - geo.top) > 2 && top < window.innerHeight) window.scrollBy(0, top - geo.top);
  }
  // Opening hides the button that was focused: move focus to the builder's title.
  function focusTitle() {
    if (!overlay) return;
    const active = document.activeElement;
    if (active && active !== document.body && root.contains(active)) return;
    const title = $('[data-gp-nb-title]');
    title.tabIndex = -1;
    title.focus({ preventScroll: true });
  }
  function alignWhenSettled(windowMs = 3000) {
    const until = performance.now() + windowMs;
    let timer = setTimeout(alignToTop, 400);
    const onScroll = () => {
      clearTimeout(timer);
      if (userScrolled || performance.now() > until) { window.removeEventListener('scroll', onScroll); return; }
      timer = setTimeout(alignToTop, 300);
    };
    on(window, 'scroll', onScroll, { passive: true });
    setTimeout(() => window.removeEventListener('scroll', onScroll), windowMs + 400);
  }
  on(document, 'gp:frame-builder-visibility', (event) => {
    if (event.detail && event.detail.visible) {
      barCandidates = null;
      updateTopOffset(true);
      if (syncFrameVariant()) render();
    }
    if (event.detail && event.detail.visible && root.dataset.gpNbShown !== 'true') {
      root.dataset.gpNbShown = 'true';
      armAlign();
      alignWhenSettled();
      focusTitle();
    } else if (event.detail && !event.detail.visible) {
      root.dataset.gpNbShown = 'false';
      shipInfo.open = false;
      if (setupToggle.getAttribute('aria-expanded') === 'true') setOpen(false);
    }
  });

  // Live stock: re-check prices and stock, so a page served from cache or left
  // open can't offer something that has since sold out. One request:
  // sections/frame-builder-stock.liquid lists every variant the builder uses as
  // [id, available, price, compareAt] rows. Runs when the builder opens, and again
  // when the cart refuses a setup (exact: no minute stamp, so nothing cached).
  // Returns { dropped, giftChanged }, or null when stock couldn't be read.
  async function refreshStock(exact) {
    let stock;
    try {
      const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      // The minute stamp keeps the answer at most a minute old, whatever the store's page cache holds.
      const stamp = exact ? Date.now() : Math.floor(Date.now() / 60000);
      const res = await fetch(`${base}products/${encodeURIComponent(data.handle)}?section_id=frame-builder-stock&t=${stamp}`, { credentials: 'same-origin' });
      if (!res.ok) return null;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      stock = new Map(JSON.parse(doc.querySelector('[data-gp-nb-stock]').textContent)
        .map(([id, available, price, compareAt]) => [Number(id), { available: !!available, price: Number(price), compareAt: Number(compareAt) || 0 }]));
    } catch (e) {
      return null; // keep the values we have
    }
    const fresh = (id) => stock.get(Number(id));
    frameVariants.forEach((v) => { const f = fresh(v.id); if (f) Object.assign(v, f); });
    const frame = fresh(data.frame.variantId);
    if (frame) Object.assign(data.frame, frame);
    addons.forEach((a) => { const v = fresh(a.variantId); if (v) Object.assign(a, v); });
    // A gift that sold out since the page was cached would make the cart reject the whole setup.
    [...giftCandidates.choose, ...giftCandidates.auto].forEach((g) => { const v = fresh(g.variantId); if (v) g.available = v.available; });
    const before = `${giftMode}:${gifts.map((g) => g.variantId)}`;
    resolveGifts();
    const giftChanged = `${giftMode}:${gifts.map((g) => g.variantId)}` !== before;
    if (giftChanged) buildGift();
    return { dropped: render(), giftChanged };
  }

  // Picks removed because they sold out: shown over the picture (not only read
  // out), as a shopper may have built the setup earlier or opened a shared link.
  function tellSoldOut(dropped) {
    const soldOut = dropped.filter((k) => byKey.get(k) && !byKey.get(k).available);
    const others = dropped.filter((k) => !soldOut.includes(k));
    if (!soldOut.length) return '';
    const one = soldOut.length === 1;
    const text = `${names(soldOut, 'and')} sold out and ${one ? 'was' : 'were'} removed from your setup.`;
    announce(`${text}${removedNote(others)} Total ${money(totals().total)}.`);
    toast(`${names(soldOut, 'and')} sold out and ${one ? 'was' : 'were'} removed`, true, 5000);
    return text;
  }

  refreshStock().then((result) => { if (result) tellSoldOut(result.dropped); });

  // Layout switcher for comparing designs: shown when previewing an unpublished
  // theme or in the theme editor (never on the live theme), or with ?layout=.
  const previewing = !!(window.Shopify && window.Shopify.theme && window.Shopify.theme.role && window.Shopify.theme.role !== 'main')
    || !!(window.Shopify && window.Shopify.designMode);
  if (previewing || layoutParam || accentParam) {
    document.querySelectorAll('.gp-nb__layouts').forEach((el) => el.remove()); // e.g. section reloaded in the theme editor
    const staff = document.createElement('div');
    staff.className = 'gp-nb__layouts';
    const link = (key, value, current, label, swatch) => {
      const url = new URL(location.href);
      url.searchParams.set(key, value);
      return `<a href="${escapeHtml(url.toString())}" data-${key}="${value}"${swatch ? ` class="gp-nb__swatch" style="--gp-nb-swatch: ${swatch}" title="${label}" aria-label="${label}"` : ''}${value === current ? ' aria-current="true"' : ''}>${swatch ? '' : label}</a>`;
    };
    const cap = (name) => name[0].toUpperCase() + name.slice(1);
    staff.innerHTML = '<button type="button" class="gp-nb__layouts-toggle" aria-expanded="false" aria-label="Design options"><svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><circle cx="10" cy="10" r="7.5" fill="none" stroke="currentColor" stroke-width="1.6"/><circle cx="7" cy="8" r="1.3" fill="currentColor"/><circle cx="11" cy="6.5" r="1.3" fill="currentColor"/><circle cx="13.5" cy="10" r="1.3" fill="currentColor"/></svg></button>'
      + `<div class="gp-nb__layouts-row gp-nb__layouts-row--layout"><span>Layout</span>${LAYOUTS.map((name) => link('layout', name, layout, cap(name))).join('')}</div>`
      + `<div class="gp-nb__layouts-row"><span>Colour</span>${Object.keys(ACCENTS).map((name) => link('accent', name, accent, cap(name), ACCENTS[name])).join('')}</div>`;
    // Phones: folded into a round button just above the bottom bar.
    const placeStaff = () => staff.style.setProperty('--gp-nb-staff-bottom', `${Math.round(bar.offsetHeight + 12)}px`);
    placeStaff();
    on(window, 'resize', placeStaff);
    staff.querySelector('.gp-nb__layouts-toggle').addEventListener('click', (event) => {
      const open = staff.classList.toggle('is-open');
      event.currentTarget.setAttribute('aria-expanded', String(open));
      placeStaff();
    });
    // Switch in place (a reload would close the builder).
    staff.addEventListener('click', (event) => {
      const a = event.target.closest('a');
      if (!a) return;
      event.preventDefault();
      if (a.dataset.layout) {
        root.classList.remove(`gp-nb--${layout}`);
        layout = a.dataset.layout;
        root.classList.add(`gp-nb--${layout}`);
        try { sessionStorage.setItem('gp-nb-layout', layout); } catch (e) { /* ignore */ }
      } else if (a.dataset.accent) {
        accent = a.dataset.accent;
        root.dataset.accent = accent;
        try { sessionStorage.setItem('gp-nb-accent', accent); } catch (e) { /* ignore */ }
      }
      a.parentElement.querySelectorAll('a').forEach((x) => (x === a ? x.setAttribute('aria-current', 'true') : x.removeAttribute('aria-current')));
      // Keep both choices in the address so a shared preview link shows the same thing.
      const url = new URL(location.href);
      url.searchParams.set('layout', layout);
      url.searchParams.set('accent', accent);
      history.replaceState(history.state, '', url.toString());
    });
    // On the page itself (not inside the builder) so no section styling can hide it.
    document.body.appendChild(staff);
  }

  buildTiles();
  buildGift();
  buildIncluded();
  buildKits();
  renderShipping();
  renderTrust();
  restoreSetup();
  render();
  checkCart();
  root.removeAttribute('style'); // the inline style kept the builder hidden until now
  updateTopOffset(true);
  document.body.dispatchEvent(new CustomEvent('gp:native-builder-mounted', { bubbles: true }));
  if (root.dataset.gpNbShown !== 'true') {
    root.dataset.gpNbShown = 'true';
    armAlign();
    alignWhenSettled();
    focusTitle();
  }
})();
